import { useMemo, type FC, useCallback } from "react";
import { Button } from "@/components/flexkit/Button";
import { Text } from "@/components/podkit/typography/Text";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import type { ArchivedEnvironment } from "@/utils/environment";
import { CornerUpLeft, Trash2, GitBranch } from "lucide-react";
import { EnvironmentName, EnvironmentChanges } from "@/routes/environments/SidebarEnvironmentList";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { Tooltip } from "@/components/Tooltip";
import { groupArchivedEnvironmentsByArchiveDate, type GroupedArchivedEnvironments } from "@/utils/environment";
import { useEnvironmentName } from "@/hooks/use-environment-name";
import { useBranch } from "@/hooks/use-branch";
import { useUnarchiveEnvironment } from "@/queries/environment-queries";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";

type ArchivedEnvironmentsListProps = {
    archivedEnvironments?: ArchivedEnvironment[];
    isLoading?: boolean;
    onDelete: (environment: ArchivedEnvironment) => void;
};

export const ArchivedEnvironmentsList: FC<ArchivedEnvironmentsListProps> = ({
    archivedEnvironments,
    isLoading,
    onDelete,
}: ArchivedEnvironmentsListProps) => {
    const groupedEnvironments: GroupedArchivedEnvironments = useMemo(() => {
        return groupArchivedEnvironmentsByArchiveDate(archivedEnvironments ?? []);
    }, [archivedEnvironments]);

    return (
        <>
            {isLoading ? (
                <div className="flex flex-grow flex-col gap-2 p-4">
                    <div className="flex flex-col items-center gap-[8px]">
                        <SkeletonBlock className="h-12 w-full" ready={false} />
                        <SkeletonBlock className="h-12 w-full" ready={false} />
                        <SkeletonBlock className="h-12 w-full" ready={false} />
                    </div>
                </div>
            ) : archivedEnvironments && archivedEnvironments.length > 0 ? (
                <ScrollArea orientation="vertical" className="flex-grow">
                    <div className="flex flex-col gap-4 py-2 pl-2 pr-2 [@supports(-moz-appearance:none)]:pr-0">
                        {groupedEnvironments.oneToTwoWeeks.length > 0 && (
                            <div className="flex flex-col gap-2 py-2">
                                <Text className="pl-[8px] text-sm font-bold text-content-secondary">1 week ago</Text>
                                {groupedEnvironments.oneToTwoWeeks.map((env) => (
                                    <ArchivedEnvironmentItem
                                        key={env.environment.id}
                                        archivedEnvironment={env}
                                        onDelete={onDelete}
                                    />
                                ))}
                            </div>
                        )}
                        {groupedEnvironments.twoWeeksToMonth.length > 0 && (
                            <div className="flex flex-col gap-2 py-2">
                                <Text className="pl-[8px] text-sm font-bold text-content-secondary">2 weeks ago</Text>
                                {groupedEnvironments.twoWeeksToMonth.map((env) => (
                                    <ArchivedEnvironmentItem
                                        key={env.environment.id}
                                        archivedEnvironment={env}
                                        onDelete={onDelete}
                                    />
                                ))}
                            </div>
                        )}
                        {groupedEnvironments.olderThanMonth.length > 0 && (
                            <div className="flex flex-col gap-2 py-2">
                                <Text className="pl-[8px] text-sm font-bold text-content-secondary">1 month ago</Text>
                                {groupedEnvironments.olderThanMonth.map((env) => (
                                    <ArchivedEnvironmentItem
                                        key={env.environment.id}
                                        archivedEnvironment={env}
                                        onDelete={onDelete}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                </ScrollArea>
            ) : (
                <EmptyArchiveCard />
            )}
        </>
    );
};

type ArchivedEnvironmentItemProps = {
    archivedEnvironment: ArchivedEnvironment;
    onDelete: (archivedEnvironment: ArchivedEnvironment) => void;
};

const ArchivedEnvironmentItem: FC<ArchivedEnvironmentItemProps> = ({ archivedEnvironment, onDelete }) => {
    const environmentName = useEnvironmentName(archivedEnvironment.environment);
    const branchName = useBranch(archivedEnvironment.environment);
    const unarchiveEnvironment = useUnarchiveEnvironment();
    const { toast } = useToast();

    const onUnarchive = useCallback(async () => {
        try {
            await unarchiveEnvironment.mutateAsync(archivedEnvironment.environment.id);
            toast({ title: "Environment unarchived" });
        } catch (err) {
            toast({
                title: "Failed to unarchive environment",
                description: formatError(err),
            });
        }
    }, [archivedEnvironment.environment.id, toast, unarchiveEnvironment]);

    return (
        <div className="group flex h-[42px] flex-row items-center rounded-md px-[8px] transition-colors hover:bg-surface-glass">
            <div className="flex min-w-0 flex-1 flex-col items-start">
                <Tooltip content={archivedEnvironment.repoFullName ?? archivedEnvironment.project?.metadata?.name}>
                    <div className="w-full truncate text-base font-bold">
                        {archivedEnvironment.repoFullName ? (
                            <span className="[direction:rtl]">{archivedEnvironment.repoFullName}</span>
                        ) : archivedEnvironment.projectMissing ? (
                            "Deleted project"
                        ) : (
                            archivedEnvironment.project?.metadata?.name || "Untitled project"
                        )}
                    </div>
                </Tooltip>
                <div className="flex w-full min-w-0 flex-row items-center gap-1 pr-1">
                    <GitBranch size={12} className="shrink-0 text-content-secondary" />
                    <EnvironmentName size="sm" name={environmentName} branchName={branchName} />
                    <EnvironmentChanges className="py-0" git={archivedEnvironment.environment.status?.content?.git} />
                </div>
            </div>
            <div className="hidden flex-row items-center gap-2 transition-opacity group-hover:flex">
                <Tooltip content="Delete environment">
                    <Button
                        size="sm"
                        variant="ghost"
                        LeadingIcon={() => <Trash2 size={16} />}
                        onClick={() => onDelete(archivedEnvironment)}
                        className="text-destructive shadow-sm group-hover:bg-surface-03"
                    />
                </Tooltip>
                <Tooltip content="Unarchive environment">
                    <Button
                        size="sm"
                        variant="ghost"
                        LeadingIcon={() => <CornerUpLeft size={16} />}
                        onClick={onUnarchive}
                        className="shadow-sm group-hover:bg-surface-03"
                        disabled={unarchiveEnvironment.isPending}
                        loading={unarchiveEnvironment.isPending}
                    />
                </Tooltip>
            </div>
        </div>
    );
};

const EmptyArchiveCard: FC = () => {
    return (
        <div className="flex flex-col gap-2 p-6">
            <div className="rounded-xl bg-surface-03 p-6">
                <div className="flex flex-col items-center gap-[8px]">
                    <div className="text-lg-bold font-bold">Nothing archived</div>
                    <div className="text-center text-base text-content-secondary">
                        Environments that are not used for 7 days are automatically archived. Archived environments will
                        be deleted based on your auto-delete setting.
                    </div>
                </div>
            </div>
        </div>
    );
};
